/**
 * Product gallery — locked. The PDP's image side: the gallery layout picker and
 * the per-variant "cohort" controls, plus the macro page layout and the gallery
 * image surface. Split out of the legacy `product-details` section so the seller
 * can configure the gallery independently of the buy box / policies / related.
 *
 * Layout is chosen with a `variant` thumbnail tile picker (mirrors the hero's
 * `variant` pattern), gating a handful of per-variant controls behind
 * `showIf: { variant: … }` so the sheet only shows knobs relevant to the pick:
 *   - `columns`  — a vertical thumb-rail slider gallery. Exposes `thumbPosition`.
 *   - `carousel` — a swipe/arrow track carousel. Exposes `carouselControls`.
 *   - `stacked`  — every image stacked down the left column.
 *   - `mosaic`   — a multi-cell image grid. Exposes `mosaicColumns`.
 *
 * All `showIf` references (thumbPosition/carouselControls/mosaicColumns → variant)
 * stay WITHIN this section. Lives under `templates.product` only.
 */
export const productGallerySchema = {
    type: "product-gallery",
    name: { ar: "معرض صور المنتج", en: "Product gallery" },
    description: {
        ar: "شكل عرض صور المنتج وتخطيط الصفحة.",
        en: "The PDP gallery: image layout, page layout, gallery surface.",
    },
    locked: true,
    criticalForSeo: true,
    maxPerTemplate: 1,
    availableOnTemplates: ["product"],
    settings: [
        {
            id: "variant",
            type: "select",
            ui: "thumbnail",
            label: { ar: "شكل صفحة المنتج", en: "Product page layout" },
            helpText: {
                ar: "اختر شكل عرض صور المنتج ومعلوماته. كل نوع له إعداداته الخاصة.",
                en: "Pick how the product gallery and info are laid out. Each type has its own settings.",
            },
            options: [
                // The current PDP: vertical thumb-rail slider + sticky info column.
                { value: "columns", label: { ar: "أعمدة (شريط صور)", en: "Columns" } },
                // Swipe/arrow track carousel, no thumb rail.
                { value: "carousel", label: { ar: "شرائح متحركة", en: "Carousel" } },
                // Every image stacked down the left column.
                { value: "stacked", label: { ar: "صور متتابعة", en: "Stacked" } },
                // EO avyro: a thumb rail beside a scrolling stack of every image.
                {
                    value: "stacked-rail",
                    label: { ar: "شريط + صور متتابعة", en: "Rail + stack" },
                },
                // Multi-cell image grid in the left column.
                { value: "mosaic", label: { ar: "فسيفساء", en: "Mosaic" } },
            ],
            default: "columns",
        },
        // --- Macro layout (shared across all gallery variants) ---------------
        {
            id: "productLayout",
            type: "select",
            label: { ar: "تخطيط الصفحة", en: "Page layout" },
            helpText: {
                ar: 'إما عمودين (صورة جنب المعلومات) أو صورة كبيرة بعرض الشاشة فوق معلومات في المنتصف (شكل معرض EasyOrders).',
                en: 'Either two columns (gallery beside the info) or a full-width hero image above centered info (EasyOrders gallery style).',
            },
            options: [
                { value: "two-column", label: { ar: "عمودين", en: "Two columns" } },
                {
                    value: "stacked-hero",
                    label: { ar: "صورة كبيرة ثم معلومات", en: "Full-width hero" },
                },
            ],
            default: "two-column",
        },
        // --- Columns cohort --------------------------------------------------
        {
            id: "thumbPosition",
            type: "select",
            label: { ar: "مكان شريط الصور", en: "Thumbnail rail position" },
            helpText: {
                ar: "مكان شريط الصور المصغّرة بالنسبة للصورة الكبيرة.",
                en: "Where the thumbnail rail sits relative to the main image.",
            },
            options: [
                { value: "left", label: { ar: "جنب الصورة", en: "Beside" } },
                { value: "below", label: { ar: "تحت الصورة", en: "Below" } },
            ],
            default: "left",
            showIf: { variant: ["columns"] },
        },
        // --- Carousel cohort -------------------------------------------------
        {
            id: "carouselControls",
            type: "select",
            label: { ar: "أدوات التنقل", en: "Carousel controls" },
            helpText: {
                ar: "شكل أدوات التنقل بين الصور: نقاط، أسهم، أو الاتنين.",
                en: "How the gallery navigation shows: dots, arrows, or both.",
            },
            options: [
                { value: "none", label: { ar: "بدون", en: "None" } },
                { value: "dots", label: { ar: "نقاط", en: "Dots" } },
                { value: "arrows", label: { ar: "أسهم", en: "Arrows" } },
                { value: "both", label: { ar: "نقاط وأسهم", en: "Both" } },
            ],
            default: "arrows",
            showIf: { variant: ["carousel"] },
        },
        // --- Mosaic cohort ---------------------------------------------------
        {
            id: "mosaicColumns",
            type: "select",
            label: { ar: "عدد أعمدة الفسيفساء", en: "Mosaic columns" },
            helpText: {
                ar: "عدد الأعمدة في شبكة الصور.",
                en: "Number of columns in the image grid.",
            },
            options: [
                { value: "2", label: { ar: "عمودين", en: "2 columns" } },
                { value: "3", label: { ar: "٣ أعمدة", en: "3 columns" } },
            ],
            default: "3",
            showIf: { variant: ["mosaic"] },
        },
        {
            id: "mosaicHeroEvery",
            type: "select",
            label: { ar: "صورة كبيرة متكررة", en: "Featured hero cadence" },
            helpText: {
                ar: "صورة كبيرة بعرض الشبكة: الأولى فقط، أو كل ٣ صور (شكل معرض التجميل).",
                en: "Full-width featured image: only the first, or every 3rd image (beauty-mosaic look).",
            },
            options: [
                { value: "first", label: { ar: "الأولى فقط", en: "First only" } },
                { value: "3", label: { ar: "كل ٣ صور", en: "Every 3rd" } },
                { value: "none", label: { ar: "شبكة متساوية", en: "Uniform grid" } },
            ],
            default: "first",
            showIf: { variant: ["mosaic"] },
        },
        {
            id: "mosaicAspect",
            type: "select",
            label: { ar: "نسبة صور الفسيفساء", en: "Mosaic image ratio" },
            helpText: {
                ar: "شكل خلايا الشبكة: طولية (٤:٥) أو مربّعة (١:١).",
                en: "Grid cell shape: portrait (4:5) or square (1:1).",
            },
            options: [
                { value: "portrait", label: { ar: "طولية", en: "Portrait" } },
                { value: "square", label: { ar: "مربّعة", en: "Square" } },
            ],
            default: "portrait",
            showIf: { variant: ["mosaic"] },
        },
        // --- Shared across all variants -------------------------------------
        {
            id: "infoColumnWidth",
            type: "select",
            label: { ar: "عرض عمود المعلومات", en: "Info column width" },
            helpText: {
                ar: "عرض عمود اسم المنتج والسعر وزر الشراء.",
                en: "Width of the name / price / add-to-cart column.",
            },
            options: [
                { value: "narrow", label: { ar: "ضيّق", en: "Narrow" } },
                { value: "medium", label: { ar: "متوسط", en: "Medium" } },
                { value: "wide", label: { ar: "واسع", en: "Wide" } },
                {
                    value: "balanced",
                    label: { ar: "متوازن (عمودين محدودين)", en: "Balanced (capped)" },
                },
            ],
            default: "medium",
        },
        // --- Per-family PDP gallery image surface (EO parity). EO tiles the product
        //     image on a soft-grey wrapper; empty ⇒ white (legacy behaviour). -------
        {
            id: "pdpImageBg",
            styling: true,
            type: "text",
            label: { ar: "خلفية صور المنتج", en: "Gallery image surface" },
            helpText: { ar: "مثال: #EEEEEE", en: "CSS color behind gallery images. Empty = white." },
            default: "",
        },
    ],
};
//# sourceMappingURL=product-gallery.js.map